import React from 'react';
import ReactDOM from 'react-dom';
import './NavigationSearch.css';

export default function NavigationSearch(props) {

	const x = props.x;

	const [query, setQuery] = React.useState('');
	const [showResults, setShowResults] = React.useState(false);
	const [resultsRect, setResultsRect] = React.useState(null);
	const [forums, setForums] = React.useState([]);
	const forumsLoaded = React.useRef(false);
	const inputRef = React.useRef(null);

	React.useEffect(() => {
		if (props.onClose) inputRef.current.focus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	function loadForums() {
		if (forumsLoaded.current) return;
		forumsLoaded.current = true;
		// x.was.foren wird bereits einmalig beim App-Start befuellt (Webapp.js) - ein
		// eigener axios-Request hier war redundant und sorgte dafuer, dass die
		// "Foren"-Gruppe bei jedem ersten Oeffnen (pro Modul-Instanz) erst mit
		// spuerbarer Verzoegerung nachtraeglich einsprang.
		// Das Hauptforum ("forum") ist kein Eintrag der foren-Tabelle, muss hier
		// also als fester Eintrag ergaenzt werden, sonst fehlt der Weg zurueck
		// zum Hauptforum, sobald man in ein anderes Modul gewechselt hat.
		const hauptforum = { id: 'forum', name: 'Hauptforum' };
		const forenList = Object.values(x.was.foren || {});
		setForums([hauptforum, ...forenList.filter((forum) => parseInt(forum.disabled, 10) !== 1)]);
	}

	// Das Portal (siehe unten) rendert ausserhalb von #webapp, wo die
	// Theme-Variablen per Inline-Style gesetzt werden (siehe WebappLayout.js).
	// CSS var(...) kaeme dort ins Leere, daher werden die aktuell gueltigen
	// Werte hier ausgelesen und dem Portal explizit mitgegeben.
	function getThemeStyle() {
		const webapp = document.getElementById('webapp');
		const computed = window.getComputedStyle(webapp);
		return {
			'--content-bg-color': computed.getPropertyValue('--content-bg-color'),
			'--content-bg-color-disabled': computed.getPropertyValue('--content-bg-color-disabled'),
			'--content-border-color': computed.getPropertyValue('--content-border-color'),
			'--forum-color': computed.getPropertyValue('--forum-color'),
		};
	}

	function openResults() {
		loadForums();
		setResultsRect(inputRef.current.getBoundingClientRect());
		setShowResults(true);
	}

	function toggleResults() {
		if (showResults) {
			reset();
		}
		else {
			openResults();
		}
	}

	function matches(name) {
		return query.trim() === '' || name.toLowerCase().includes(query.trim().toLowerCase());
	}

	const filteredForums = forums
		.filter((forum) => String(forum.id) !== String(x.wam.current))
		.filter((forum) => matches(forum.name));
	const filteredMembers = Object.keys(x.was.members)
		.map((memberId) => ({ memberId, ...x.was.members[memberId] }))
		.filter((member) => parseInt(member.memberId, 10) !== parseInt(x.was.memberId, 10))
		.filter((member) => parseInt(member.locked, 10) !== 1)
		.filter((member) => matches(member.name));
	function getModuleTitle(moduleId) {
		// Der Titel steht zuverlaessig schon in der Tag-Definition (Content.js), unabhaengig
		// davon, ob das Modul in dieser Sitzung bereits gemountet wurde (x.wam[id].title
		// existiert erst nach dem ersten Rendern des Moduls und ist bis dahin ein
		// generischer Platzhalter).
		const tag = x.wam.tags && x.wam.tags[moduleId];
		if (tag && typeof tag.props.title !== 'undefined') return tag.props.title;
		if (typeof x.wam[moduleId] !== 'undefined' && typeof x.wam[moduleId].title !== 'undefined') return x.wam[moduleId].title;
		return null;
	}

	const filteredFavoriteModules = (x.was.favoriteModules || [])
		.filter((moduleId) => String(moduleId) !== String(x.wam.current))
		.map((moduleId) => ({ moduleId, title: getModuleTitle(moduleId) }))
		.filter((favoriteModule) => favoriteModule.title !== null)
		.filter((favoriteModule) => matches(favoriteModule.title));

	// Bereits geoeffnete Module (unabhaengig von Favoriten) - Foren werden hier
	// ausgeschlossen, da sie schon in der Foren-Gruppe stehen, ebenso das aktuell
	// aktive Modul und alles, was schon unter "Weitere favorisierte Module" steht.
	const filteredOpenModules = Object.entries(x.wam)
		.filter(([, value]) => typeof value === 'object' && value !== null && typeof value.moduleType !== 'undefined')
		.filter(([, value]) => value.moduleType !== 'system' && value.moduleType !== 'forum')
		.filter(([moduleId]) => moduleId !== x.wam.current)
		.filter(([moduleId]) => !(x.was.favoriteModules || []).includes(moduleId))
		.map(([moduleId, value]) => ({ moduleId, title: value.title }))
		.filter((openModule) => typeof openModule.title !== 'undefined')
		.filter((openModule) => matches(openModule.title));

	function openForum(forum) {
		x.wam.openForum(String(forum.id), forum.name);
		reset();
		if (props.onClose) props.onClose();
	}

	function openMember(member) {
		x.wam.openPrivateForum(member.memberId, member.name, member.level, true);
		reset();
		if (props.onClose) props.onClose();
	}

	function openFavoriteModule(favoriteModule) {
		x.wam.activate(favoriteModule.moduleId);
		reset();
		if (props.onClose) props.onClose();
	}

	function openOpenModule(openModule) {
		x.wam.activate(openModule.moduleId);
		reset();
		if (props.onClose) props.onClose();
	}

	function reset() {
		setQuery('');
		setShowResults(false);
	}

	const isCompact = props.variant === 'compact';

	const results = (showResults && resultsRect) &&
		<div
			className={`navigation-search-results ${isCompact ? 'navigation-search-results-compact' : ''}`}
			style={{ top: resultsRect.bottom + 4, left: resultsRect.left, width: isCompact ? 'auto' : resultsRect.width, ...getThemeStyle() }}
		>
			{filteredForums.length > 0 &&
				<div className="navigation-search-group-label">Foren</div>
			}
			{filteredForums.map((forum) =>
				<div
					key={'forum-' + forum.id}
					className="navigation-search-result navigation-search-result-forum"
					onMouseDown={() => openForum(forum)}
				>
					<i className="bi bi-chat-square-text navigation-search-result-icon" />
					{forum.name}
				</div>
			)}
			{filteredFavoriteModules.length > 0 &&
				<div className="navigation-search-group-label">Weitere favorisierte Module</div>
			}
			{filteredFavoriteModules.map((favoriteModule) =>
				<div
					key={'favorite-' + favoriteModule.moduleId}
					className="navigation-search-result navigation-search-result-favorite"
					onMouseDown={() => openFavoriteModule(favoriteModule)}
				>
					<i className="bi bi-star-fill navigation-search-result-icon" />
					{favoriteModule.title}
				</div>
			)}
			{filteredOpenModules.length > 0 &&
				<div className="navigation-search-group-label">Geöffnete Module</div>
			}
			{filteredOpenModules.map((openModule) =>
				<div
					key={'open-' + openModule.moduleId}
					className="navigation-search-result navigation-search-result-open"
					onMouseDown={() => openOpenModule(openModule)}
				>
					<i className="bi bi-window navigation-search-result-icon" />
					{openModule.title}
				</div>
			)}
			{filteredMembers.length > 0 &&
				<div className="navigation-search-group-label">Mitglieder</div>
			}
			{filteredMembers.map((member) =>
				<div
					key={'member-' + member.memberId}
					className="navigation-search-result navigation-search-result-member"
					onMouseDown={() => openMember(member)}
				>
					<i className="bi bi-person navigation-search-result-icon" />
					{member.name}
				</div>
			)}
			{filteredForums.length === 0 && filteredMembers.length === 0 && filteredFavoriteModules.length === 0 && filteredOpenModules.length === 0 &&
				<div className="navigation-search-result navigation-search-no-result">Keine Treffer</div>
			}
		</div>

	if (isCompact) {
		return (
			<div id="navigation-search" className="navigation-search navigation-search-compact">
				<span
					ref={inputRef}
					role="button"
					tabIndex={0}
					className="navigation-search-compact-trigger"
					onClick={toggleResults}
					onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleResults(); } }}
					onBlur={() => setTimeout(reset, 200)}
				>
					<span className="navigation-search-compact-label">{props.triggerLabel}</span>
					<i className="bi bi-chevron-down navigation-search-compact-icon" />
				</span>
				{results && ReactDOM.createPortal(results, document.body)}
			</div>
		);
	}

	return (
		<div id="navigation-search" className="navigation-search">
			<div className="navigation-search-box">
				<input
					ref={inputRef}
					type="text"
					className="navigation-search-input"
					placeholder="Foren- & Mitgliederliste"
					value={query}
					onChange={(e) => setQuery(e.target.value)}
					onFocus={openResults}
					onBlur={() => setTimeout(reset, 200)}
				/>
				{props.onClose
					? <i
						className="bi bi-x-lg navigation-search-icon navigation-search-close"
						onMouseDown={(e) => {
							e.preventDefault();
							props.onClose();
						}}
						onTouchEnd={(e) => {
							// Auf Touch-Geraeten explizit statt auf die (teils verzoegerte/
							// inkonsistente) synthetische Maus-Event-Emulation zu vertrauen -
							// preventDefault verhindert zusaetzlich, dass danach noch ein
							// synthetisches mousedown/click ausgeloest wird (sonst doppelt).
							e.preventDefault();
							props.onClose();
						}}
					/>
					: <i className="bi bi-signpost-2 navigation-search-icon" />
				}
			</div>
			{results && ReactDOM.createPortal(results, document.body)}
		</div>
	);

}
